'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'

/**
 * Dynamic "Sign in" / "Account" link.
 *
 * Renders "Sign in" by default (safe default, works server-side too).
 * On mount, fetches /api/platform/tier to detect session. If authenticated,
 * swaps label to "Account" and redirects to /platform/account instead of
 * /platform/sign-in.
 *
 * Used everywhere we have a sign-in affordance so the label matches state
 * universally (header, pricing page, landing page, app access gate, modals).
 *
 * Intentional design choice: "Account" not "Sign out". Sign-out lives
 * safely inside the account page to avoid misclick session loss.
 */
export function SignInLink({
  className,
  style,
  signedOutLabel = 'Sign in',
  signedInLabel = 'Account',
  signedOutHref = '/platform/sign-in',
  signedInHref = '/platform/account',
}: {
  className?: string
  style?: React.CSSProperties
  signedOutLabel?: string
  signedInLabel?: string
  signedOutHref?: string
  signedInHref?: string
}) {
  const [isSignedIn, setIsSignedIn] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/platform/tier')
      .then(r => r.json())
      .then(d => {
        if (!cancelled && d?.email) setIsSignedIn(true)
      })
      .catch(() => {
        // Silent — keep default signed-out state
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Link
      href={isSignedIn ? signedInHref : signedOutHref}
      className={className}
      style={style}
    >
      {isSignedIn ? signedInLabel : signedOutLabel}
    </Link>
  )
}
